// Your backyard: a little garden behind your home (through its back door), a private copy for each character.
// Plant seeds in the soil plots and they grow on the real clock (sprout, leafy, ripe); water them with a watering
// can to halve what's left of a stage (once per stage). Hens lay eggs, the cow gives milk and sheep grow wool, as
// long as they were fed in the last day (they never come to harm: they just stop producing). A seed and feed
// vendor in every homeland's town sells more. Friends visiting your home can come out and look.
import { T, ZoneBuilder, zoneMap, house } from './zonebuild.js';
import type { ZoneDef } from './zones.js';

// ---------- crops ----------
export interface CropDef { id: string; name: string; seed: string; produce: string; growMs: number; yield: [number, number]; seedBack: number; color: string }
const MIN = 60_000;
export const CROPS: CropDef[] = [
  { id: 'carrot', name: 'Carrots', seed: 'carrot_seed', produce: 'carrot', growMs: 20 * MIN, yield: [2, 3], seedBack: 0.5, color: '#f28b4b' },
  { id: 'turnip', name: 'Turnips', seed: 'turnip_seed', produce: 'turnip', growMs: 40 * MIN, yield: [2, 3], seedBack: 0.4, color: '#f2f0e6' },
  { id: 'strawberry', name: 'Strawberries', seed: 'strawberry_seed', produce: 'strawberry', growMs: 60 * MIN, yield: [3, 5], seedBack: 0.35, color: '#e3604b' },
  { id: 'pumpkin', name: 'Pumpkins', seed: 'pumpkin_seed', produce: 'pumpkin', growMs: 120 * MIN, yield: [1, 2], seedBack: 0.3, color: '#f2a03c' },
];
export const cropDef = (id: string | null | undefined) => CROPS.find(c => c.id === id);
export const cropForSeed = (seed: string) => CROPS.find(c => c.seed === seed);
export type Stage = 'empty' | 'sprout' | 'leafy' | 'ripe';
/** Where a crop is: how far it has grown (0..1, counting the time watering saved) and its stage. */
export function growth(p: { crop: string | null; plantedAt: number; bonusMs: number }, now: number): { k: number; stage: Stage; leftMs: number } {
  const c = cropDef(p.crop); if (!c) return { k: 0, stage: 'empty', leftMs: 0 };
  const k = Math.max(0, Math.min(1, (now - p.plantedAt + p.bonusMs) / c.growMs));
  return { k, stage: k >= 1 ? 'ripe' : k < 1 / 3 ? 'sprout' : 'leafy', leftMs: Math.max(0, c.growMs - (now - p.plantedAt + p.bonusMs)) };
}

// ---------- animals ----------
export type AnimalKind = 'hen' | 'cow' | 'sheep';
export interface AnimalDef { kind: AnimalKind; name: string; produce: string; everyMs: number; max: number; pen: 'coop' | 'paddock' }
const HOUR = 60 * MIN;
export const ANIMALS: Record<AnimalKind, AnimalDef> = {
  hen: { kind: 'hen', name: 'Hen', produce: 'egg', everyMs: 3 * HOUR, max: 4, pen: 'coop' },
  cow: { kind: 'cow', name: 'Cow', produce: 'milk', everyMs: 24 * HOUR, max: 1, pen: 'paddock' },
  sheep: { kind: 'sheep', name: 'Sheep', produce: 'wool', everyMs: 24 * HOUR, max: 2, pen: 'paddock' },
};
/** Fed within this long and they keep producing. */
export const FED_MS = 24 * HOUR;
/** Is it ready to give its egg (milk, wool)? Only when it's had its feed in the last day. */
export const animalReady = (a: { kind: AnimalKind; fedAt: number; lastCollect: number }, now: number) =>
  now - a.lastCollect >= ANIMALS[a.kind].everyMs && now - a.fedAt <= FED_MS;
export const animalHungry = (a: { fedAt: number }, now: number) => now - a.fedAt > FED_MS;
/** Cozy names for new animals. */
export const ANIMAL_NAMES: Record<AnimalKind, string[]> = {
  hen: ['Pip', 'Clucky', 'Dumpling', 'Biscuit', 'Nugget', 'Hazel', 'Penny'],
  cow: ['Buttercup', 'Daisy', 'Moomin', 'Clover'],
  sheep: ['Woolly', 'Cloud', 'Pudding', 'Fluff'],
};

// ---------- the vendor ----------
/** What a seed and feed vendor sells (gil each). `animal:` entries are animals for your backyard. */
export const GARDEN_SHOP: { item: string; price: number; label?: string }[] = [
  { item: 'carrot_seed', price: 5 }, { item: 'turnip_seed', price: 8 }, { item: 'strawberry_seed', price: 15 }, { item: 'pumpkin_seed', price: 30 },
  { item: 'animal_feed', price: 4 }, { item: 'watering_can', price: 20 },
  { item: 'animal:hen', price: 60, label: 'A hen' }, { item: 'animal:sheep', price: 150, label: 'A sheep' }, { item: 'animal:cow', price: 300, label: 'A cow' },
];
/** The seed and feed vendors, one in each homeland's town (where they stand, tiles). */
export const VENDORS: { name: string; map: string; at: [number, number] }[] = [
  { name: 'Seedkeeper Poppy', map: 'cottages', at: [10, 12] },
  { name: 'Seedwarden Bryn', map: 'elderglade', at: [8, 18] },
  { name: 'Grainmonger Tess', map: 'lanternport', at: [10, 16] },
];
export const VENDOR_RANGE_TILES = 4;
/** The neighbour's welcome gift, the first time you step into your backyard. */
export const GARDEN_GIFT = { items: [{ def: 'carrot_seed', qty: 6 }, { def: 'watering_can', qty: 1 }, { def: 'animal_feed', qty: 3 }], animal: 'hen' as AnimalKind };

// ---------- the backyard itself ----------
export const GARDEN_W = 24, GARDEN_H = 18;
/** The soil plots (tiles), the hens' run and the paddock (tile rectangles: x0, y0, x1, y1, inside the fence). */
export const PLOTS: [number, number][] = [[3, 7], [5, 7], [7, 7], [9, 7], [3, 10], [5, 10], [7, 10], [9, 10]];
export const COOP_RUN: [number, number, number, number] = [15, 7, 20, 8];
export const PADDOCK: [number, number, number, number] = [15, 11, 20, 14];
/** The back door of the cottage, seen from the garden (a door tile), and how close you must be to a plot or a pen. */
export const GARDEN_DOOR: [number, number] = [11, 3];
export const GARDEN_RANGE_PX = 22, PEN_RANGE_PX = 40;
/** The back door inside the cottage (a door in the back wall). */
export const BACK_DOOR: [number, number] = [4, 2];

function garden() {
  const b = new ZoneBuilder(GARDEN_W, GARDEN_H);
  // the back of your cottage, and a path from its back door down through the garden
  b.house(8, 0, house(6, 4, '#c9705e', '#a5574a', 3, '', 'Home'));
  b.fill(11, 4, 11, 15, T.PATH).fill(3, 12, 11, 12, T.PATH).fill(12, 9, 13, 9, T.PATH);
  // trees and hedges behind, a fence all round
  for (let x = 0; x < GARDEN_W; x++) for (let y = 0; y < 4; y++) if (x < 8 || x > 13) b.block(x, y);
  for (const [x, y] of [[1, 1], [3, 0], [5, 2], [15, 1], [21, 0], [22, 2]] as [number, number][]) b.tree(x, y, x % 2 ? 'blossom' : 'oak');
  b.put('bush', 6, 3).put('bush', 15, 3);
  b.fenceH(0, 23, 17).fenceV(0, 4, 16).fenceV(23, 4, 16);
  // the soil plots
  for (const [x, y] of PLOTS) b.fill(x, y, x, y, T.SOIL);
  b.put('sign', 2, 6);
  // the hens' coop and run
  b.house(15, 4, house(3, 2, '#e0a94e', '#b07a2e', 1, '', 'Coop'));
  b.fenceH(14, 21, 9, [14]).fenceV(14, 6, 8).fenceV(21, 6, 8);
  // the paddock
  b.fenceH(14, 21, 10).fenceH(14, 21, 15).fenceV(14, 11, 14).fenceV(21, 11, 14);
  b.put('crate', 20, 16, 0).put('barrel', 13, 16, 1);
  // a little shed and a pond
  b.house(18, 0, house(3, 3, '#7a8a6a', '#5a6a4e', 1, '', 'Shed'));
  b.ellipse(5.5, 14.5, 2.6, 1.6, T.WATER).put('lily', 5, 14, 0, false).put('reeds', 3, 15, 0, false);
  b.put('pot', 12, 5, 1).put('pot', 10, 5, 2).put('bench', 16, 16);
  b.scatter(17, 22, ['tuft', 'tuft', 'mushroom'], [1, 5, 22, 16], []);
  const out = zoneMap('garden', 'Your Garden', b, [11, 6]);
  return { ...out };
}
const g = garden();
export const GARDEN_ZONE: ZoneDef = { id: 'garden', name: 'Your Garden', kind: 'town', mounts: false, flying: false, map: g.data, art: g.art, wayshrine: null, mobs: [] };

// ---------- what the client is told ----------
export interface PlotView { plot: number; crop: string | null; plantedAt: number; bonusMs: number; watered: number }
export interface AnimalView { id: number; kind: AnimalKind; name: string; fedAt: number; lastCollect: number }
/** What you ask of your garden. */
export type GardenOp = { op: 'plant'; plot: number; seed: string } | { op: 'water' | 'harvest'; plot: number } | { op: 'feed' | 'collect'; animal: number } | { op: 'name'; animal: number; name: string };
export interface GardenView { owner: number; ownerName: string; mine: boolean; plots: PlotView[]; animals: AnimalView[]; now: number }
